/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { studentService } from '../../services';
import { useAppDispatch } from '../../hooks';
import { notificationActions } from '../../redux/actions';
import { useNavigate } from 'react-router-dom';

const SectionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px;
    width: 100%;
    min-width: 0;
    max-width: 100vw;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;

  @media (max-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const StatsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 0;
`;

const StatCard = styled.div<{ bg: string; color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};

  .stat-label { opacity: 0.85; }
  .stat-value { font-size: 14px; font-weight: 700; }
`;

const ActionContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const RetryButton = styled.button`
  background: linear-gradient(90deg, #007bff 0%, #00bfff 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  white-space: nowrap;
  width: fit-content;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: fit-content;
    padding: 9px 14px;
    font-size: 12px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px 20px;
  color: #718096;

  .empty-icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  .empty-text {
    font-size: 16px;
    margin-bottom: 8px;
  }

  .empty-subtext {
    font-size: 14px;
    color: #a0aec0;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px 20px;

  .spinner {
    border: 4px solid #e2e8f0;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface WrongQuestionsStatsProps {
  userId?: number;
}

const WrongQuestionsStats: React.FC<WrongQuestionsStatsProps> = ({ userId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [retryLoading, setRetryLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        if (!userId) return;

        const response = await studentService.getWrongQuestionsStats();
        console.log('Wrong questions stats:', response);
        setStats(response);
      } catch (error: any) {
        console.error('Error fetching wrong questions stats:', error);
        // It's ok if there are no wrong questions yet
        if (error?.response?.status === 404) {
          setStats({
            totalExams: 0,
            totalCorrect: 0,
            totalWrong: 0,
            wrongQuestionsCount: 0,
            wrongQuestions: [],
          });
        } else {
          dispatch(notificationActions.addNotification(
            'Lỗi khi tải thống kê câu sai'
          ));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [userId, dispatch]);

  const handleRetryWrongQuestions = async () => {
    try {
      setRetryLoading(true);
      if (!stats || stats.wrongQuestionsCount === 0) {
        dispatch(notificationActions.addNotification(
          'Hiện không có câu sai để luyện tập',
          'INFO'
        ));
        setRetryLoading(false);
        return;
      }

      // Fetch retry exam questions
      const retryExam = await studentService.getRetryExamQuestions();
      console.log('Retry exam:', retryExam);

      // Navigate to retry exam page with exam data
      navigate('/retry-exam', { state: { retryExam } });
    } catch (error: any) {
      console.error('Error starting retry exam:', error);
      const errorMessage = typeof error === 'string' ? error : error?.response?.data?.message || error?.message || 'Lỗi khi bắt đầu luyện tập';
      dispatch(notificationActions.addNotification(
        errorMessage
      ));
      setRetryLoading(false);
    }
  };

  if (loading) {
    return (
      <SectionCard>
        <SectionTitle>📊 Thống Kê</SectionTitle>
        <LoadingSpinner>
          <div className="spinner"></div>
        </LoadingSpinner>
      </SectionCard>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitle>📊 Thống kê bài tập</SectionTitle>
        {stats.wrongQuestionsCount > 0 && (
          <RetryButton
            onClick={handleRetryWrongQuestions}
            disabled={retryLoading}
          >
            📚 {retryLoading ? 'Đang tải...' : `Luyện Tập ${stats.wrongQuestionsCount} Câu Sai`}
          </RetryButton>
        )}
      </SectionHeader>

      <StatsGrid>
        <StatCard bg="#f0fdf4" color="#16a34a">
          <span className="stat-label">Tổng Bài Làm:</span>
          <span className="stat-value">{stats.totalExams}</span>
        </StatCard>

        <StatCard bg="#eff6ff" color="#2563eb">
          <span className="stat-label">Câu Đúng:</span>
          <span className="stat-value">{stats.totalCorrect}</span>
        </StatCard>

        <StatCard bg="#fff1f2" color="#e11d48">
          <span className="stat-label">Câu Sai:</span>
          <span className="stat-value">{stats.totalWrong}</span>
        </StatCard>

        <StatCard
          bg={stats.wrongQuestionsCount > 0 ? '#fffbeb' : '#f0fdf4'}
          color={stats.wrongQuestionsCount > 0 ? '#d97706' : '#16a34a'}
        >
          <span className="stat-label">Cần Luyện Lại:</span>
          <span className="stat-value">{stats.wrongQuestionsCount}</span>
        </StatCard>
      </StatsGrid>
    </SectionCard>
  );
};

export default WrongQuestionsStats;
